import {
    ApolloClient,
    ApolloClientOptions,
    InMemoryCache,
    HttpLink,
    ApolloLink,
    NormalizedCacheObject,
    DocumentNode,
    defaultDataIdFromObject,
} from '@apollo/client';
import {
    CoachNode,
    MeditationNode,
    ProgramNode,
    ScheduledContentLinkNode,
    ScheduledContentNode,
} from 'src/generated/webapp_gql';
import programResolvers from 'src/modules/newPrograms/resolvers';
import meditationResolvers from 'src/modules/meditations/resolvers';
import introspectionQueryResultData from 'src/generated/fragmentTypesV3.json';
import { authenticationService } from './rootService';

import ApolloTypeDefs from './apollo.graphql';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { captureException } from 'src/shared/helpers/captureException';

const authLink = setContext(async (_, { options }) => {
    const headers = { ...options };
    const authData = await authenticationService.getValidAuthDataOrRefresh();

    if (authData) {
        headers.Authorization = `${authData.tokenType} ${authData.accessToken}`;
    }

    return { headers };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        if (Array.isArray(graphQLErrors)) {
            graphQLErrors.forEach(error => {
                captureException(error);
            });
        } else {
            captureException(graphQLErrors);
        }

        graphQLErrors.map(({ message, locations, path }) =>
            // eslint-disable-next-line no-console
            console.info(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            ),
        );
    }
    if (networkError) {
        captureException(networkError);
        // eslint-disable-next-line no-console
        console.info(`[Network error]: ${networkError}`);
    }
});

/**
 * Used to represent a type with a unique ID under a 'uuid' key.
 *
 * Constructed from the union of the nodes with a UUID in our
 * dataIdFromObject hook.
 */
type UuidGetterNode =
    | MeditationNode
    | ProgramNode
    | ScheduledContentNode
    | ScheduledContentLinkNode
    | CoachNode;

export interface AppClientOptions extends ApolloClientOptions<NormalizedCacheObject> {
    typeDefs: DocumentNode;
}

export type AppClient = ApolloClient<NormalizedCacheObject>;

export const buildAppClientOptions = (): AppClientOptions => {
    const cache = new InMemoryCache({
        possibleTypes: introspectionQueryResultData.possibleTypes,
        dataIdFromObject: object => {
            switch (object.__typename) {
                case 'PageNode':
                    return `${object.id}`;
                case 'ProgramNode':
                case 'MeditationNode':
                case 'ScheduledContentLinkNode':
                case 'ScheduledContentNode':
                case 'CoachNode':
                    return (object as UuidGetterNode).uuid;
                default:
                    return defaultDataIdFromObject(object);
            }
        },
        typePolicies: {
            Query: {
                fields: {
                    completedScheduledContent: {
                        merge(existing = [], incoming) {
                            const newEdges = incoming.edges.map(e => e.node.__ref);
                            const existingFiltered = (existing?.edges ?? []).filter(
                                e => !newEdges.includes(e.node.__ref),
                            );

                            // we want to keep previously cached DP and add new one
                            // we use page info from existing when it is available because it means we have more pages in cache and need to keep this info
                            return {
                                ...incoming,
                                edges: [...incoming.edges, ...existingFiltered],
                                pageInfo:
                                    existing.pageInfo && existingFiltered.length > 0
                                        ? existing.pageInfo
                                        : incoming.pageInfo,
                            };
                        },
                    },
                },
            },
        },
    });

    return {
        cache,
        typeDefs: ApolloTypeDefs,
        resolvers: {
            ...programResolvers,
            ...meditationResolvers,
        },
    };
};

export const createAppClient = (): AppClient => {
    const httpLink = new HttpLink({
        uri: process.env.REACT_APP_GRAPHQL_URL,
    });

    const link = ApolloLink.from([errorLink, authLink, httpLink]);

    return new ApolloClient({
        link,
        ...buildAppClientOptions(),
    });
};

export const createMswTestClient = (): AppClient => {
    return new ApolloClient({
        uri: 'http://test.local/graphql',
        ...buildAppClientOptions(),
    });
};

export let appClient: AppClient;
if (process.env.NODE_ENV === 'test') {
    appClient = createMswTestClient();
} else {
    appClient = createAppClient();
}
